// components/ResponsiveImage.tsx

// Define the available size variants and their actual pixel widths
const SIZE_MAP: Record<string, number> = {
	small: 480,
	medium: 960,
	large: 1440,
};

const BREAKPOINT_MAP: Partial<Record<SizeName, number>> = {
	small: 600,
	medium: 1024,
};

type SizeName = keyof typeof SIZE_MAP;
type Format = "avif" | "webp" | "jpeg" | "png";

type ResponsiveImageProps = {
	/** Base image name without size or extension (e.g., "writers") */
	baseName?: string;

	/** Custom sizes to use, defaults to ["small", "medium", "large"] */
	sizes?: SizeName[];

	/** Formats by preference (first is most preferred), default: avif > webp > jpeg */
	formats?: Format[];

	/** Custom base URL (if using custom domain) */
	baseUrl?: string;

	/** Replaces baseUrl and baseName with a full image URL */
	urlStub?: string;

	/** <img> alt text */
	alt?: string;

	/** Optional sizes attribute for responsive behavior */
	sizesAttr?: string;

	/** <img> class name */
	className?: string;

	/** Optional loading strategy */
	loading?: "eager" | "lazy";

	/** Optional decoding strategy */
	decoding?: "sync" | "async" | "auto";

	/** Optional class name for the <picture> element */
	pictureClassName?: string;
};

const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
	baseName,
	sizes = ["small", "medium", "large"],
	formats = ["avif", "webp", "jpeg", "png"],
	baseUrl = "https://images.coffitivity.com",
	urlStub,
	alt = "",
	sizesAttr,
	className = "",
	loading = "lazy",
	decoding = "async",
	pictureClassName = "",
}) => {
	// Build a srcset string based on available sizes and format
	const imageUrl = urlStub ? urlStub : `${baseUrl}/${baseName}`;
	const buildSrcSet = (format: Format) =>
		sizes
			.map((size) => `${imageUrl}-${size}.${format} ${SIZE_MAP[size]}w`)
			.join(", ");

	const fallbackFormat = formats[formats.length - 1];
	const fallbackSize = "medium"; // default fallback size for <img src>
	const fallbackSrc = `${imageUrl}-${fallbackSize}.${fallbackFormat}`;
	const fallbackSrcSet = buildSrcSet(fallbackFormat);

	const activeSizes = sizes.length
		? sizes
		: (["small", "medium", "large"] as SizeName[]);

	const computedSizesAttr = activeSizes
		.map((size, index) => {
			const width = SIZE_MAP[size];
			const breakpoint = BREAKPOINT_MAP[size];

			if (index === activeSizes.length - 1 || !breakpoint) {
				return `${width}px`;
			}

			return `(max-width: ${breakpoint}px) ${width}px`;
		})
		.join(", ");

	return (
		<picture className={pictureClassName}>
			{/* Source sets for modern formats (except fallback) */}
			{formats.slice(0, -1).map((format) => (
				<source
					key={format}
					type={`image/${format}`}
					srcSet={buildSrcSet(format)}
					sizes={sizesAttr ?? computedSizesAttr}
				/>
			))}

			{/* Fallback <img> */}
			<img
				src={fallbackSrc}
				srcSet={fallbackSrcSet}
				sizes={sizesAttr ?? computedSizesAttr}
				alt={alt}
				className={className}
				loading={loading}
				decoding={decoding}
			/>
		</picture>
	);
};

export default ResponsiveImage;
